<template>
  <div>
    <div
      v-if="state !== 'allowed'"
      class="mx-auto my-8 max-w-2xl rounded-2xl bg-white p-6 text-center
        shadow-sm ring-2 ring-teal-100 sm:p-8"
      role="status"
      :aria-busy="state === 'checking'"
    >
      <LockClosedIcon class="mx-auto mb-4 h-12 w-12 rounded-xl bg-teal-50 p-3 text-teal-600" />
      <h2 class="text-lg font-semibold text-gray-900">
        {{ t(`paidFeatures.titles.${state === 'subscriptionRequired' ? feature : state}`) }}
      </h2>
      <p class="mt-3 text-sm text-gray-600">
        {{ t(`paidFeatures.descriptions.${state === 'subscriptionRequired' ? feature : state}`) }}
      </p>
      <p
        v-if="state === 'subscriptionRequired'"
        class="mt-4 rounded-xl bg-teal-50 p-3 text-sm text-teal-800"
      >
        {{ t('paidFeatures.creditsExplanation') }}
      </p>
      <div class="mt-6 flex flex-wrap justify-center gap-3">
        <RouterLink
          v-if="state === 'subscriptionRequired'"
          :to="{ name: 'PricingPage' }"
          class="rounded-lg bg-teal-600 px-5 py-3 text-sm font-semibold text-white
            hover:bg-teal-700"
        >
          {{ t('paidFeatures.viewPlans') }}
        </RouterLink>
        <RouterLink
          v-if="state === 'unauthorized'"
          :to="{ name: 'SessionLoginPage', query: { redirect: route.fullPath } }"
          class="rounded-lg bg-teal-600 px-5 py-3 text-sm font-semibold text-white"
        >
          {{ t('paidFeatures.signIn') }}
        </RouterLink>
        <button
          v-if="state !== 'checking'"
          class="rounded-lg border border-gray-200 px-5 py-3 text-sm font-semibold
            text-gray-700 hover:bg-gray-50"
          @click="verify"
        >
          {{ t(state === 'subscriptionRequired'
            ? 'paidFeatures.checkAgain' : 'paidFeatures.retry') }}
        </button>
      </div>
    </div>
    <!-- Preserve open editors and unsaved drafts if access changes mid-edit. -->
    <div
      v-if="hasLoaded"
      v-show="state === 'allowed'"
      :class="contentClass"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { LockClosedIcon } from '@heroicons/vue/24/outline';
import usePaidAccess, { PaidFeature } from '@/store/paidAccess';
import useAuthStore from '@/store/auth';

const props = withDefaults(defineProps<{
  feature: PaidFeature;
  contextKey?: string;
  contentClass?: string;
}>(), { contextKey: '', contentClass: '' });
const emit = defineEmits(['allowed']);
const { t } = useI18n();
const route = useRoute();
const access = usePaidAccess();
const auth = useAuthStore();
const state = computed(() => access.states[props.feature]);
const hasLoaded = ref(false);

async function verify() {
  if (await access.check(props.feature)) {
    hasLoaded.value = true;
    emit('allowed');
  }
}

watch(() => [props.feature, props.contextKey, auth.session.uuid], () => {
  hasLoaded.value = false;
  verify();
}, { immediate: true });
</script>
